import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import {
  Compare as CompareIcon,
  NewReleases as NewIcon,
  History as OldIcon,
} from '@mui/icons-material';

// V2 컴포넌트들
import OrderBookInfoV2 from './OrderBookInfoV2';
import OrderBookSummaryV2 from './OrderBookSummaryV2';
import SalesProfitInfoV2 from './SalesProfitInfoV2';
import ApprovalLineFormV2 from './ApprovalLineFormV2';
import { InfoCard, StatusChip, ProfitVisualization } from './CommonComponents';

// 기존 컴포넌트들
import OrderBookInfo from '../OrderBookInfo';
import OrderBookSummary from '../OrderBookSummary';
import SalesProfitInfo from '../SalesProfitInfo';
import OrderBookApprovalForm from '../OrderBookApprovalForm';

/**
 * UI 비교 데모 페이지
 * 기존 UI와 개선된 UI(V2)를 나란히 비교할 수 있는 페이지
 */
const UIComparisonDemo = () => {
  const [viewMode, setViewMode] = useState('split'); // 'split', 'old', 'new'
  const [selectedApprUuid, setSelectedApprUuid] = useState(null);

  // 목업 데이터
  const mockProject = {
    prjUuid: 'mock-uuid-001',
    prjTyp: 'N', // 신규
    cmpGrade: 'A',
    cstGrade: 'B',
    totalSales: 150000000,
    totalPurchase: 100000000,
    totalProfit: 50000000,
  };

  const mockOrderInfo = {
    roUuid: 'ro-uuid-001',
    roNo: 'RO-2024-0001',
    prjUuid: 'mock-uuid-001',
    prjNo: 'PJT-2024-0001',
    prjNm: '스마트팩토리 MES 시스템 구축',
    partnerNm: '(주)한국전자',
    partnerUuid: 'partner-uuid-001',
    crtNm: '홍길동',
    crtUsr: 'user-uuid-001',
    crtDt: '2024-01-15',
    ordYn: 'S', // 진행중
    expDay: '2024-01-10',
    siDt: '2024-12-31',
    dlvryDuedate: '2024-06-30',
    inspectCond: '검수 후 30일 이내 지급',
    mtnFreeTm: 12,
    mtnFreeStrtDt: '2025-01-01',
    mtnFreeEndDt: '2025-12-31',
    licStrtDt: '2024-01-01',
    licEndDt: '2026-12-31',
    collCondt: '세금계산서 발행 후 익월말',
    payCondt: '현금',
    giPrePct: 10,
    giCovPct: 15,
    giMtnPct: 5,
    comment: '본 프로젝트는 스마트팩토리 MES 시스템 구축 사업입니다.\n- 1단계: 요구사항 분석 (1월~2월)\n- 2단계: 설계 및 개발 (3월~8월)\n- 3단계: 테스트 및 안정화 (9월~11월)\n- 4단계: 운영 이관 (12월)',
    roProfitAmt: 50000000,
    roProfitAmtPercent: 33.3,
    roCostAmt: 5000000,
    roCostAmtPercent: 3.3,
    roAdjProfitAmt: 40000000,
    roAdjProfitAmtPercent: 26.7,
    roInternalCosts: [
      {
        roPartnerUsrUuid: 'internal-001',
        roPartnerUsrNm: '김영업',
        roInternalAmt: 3000000,
        roInternalAmtPercent: 2,
      },
      {
        roPartnerUsrUuid: 'internal-002',
        roPartnerUsrNm: '이협력',
        roInternalAmt: 2000000,
        roInternalAmtPercent: 1.3,
      },
    ],
  };

  const mockApprovalList = [
    { docuUuid: 'docu-001', docuStat: 'Y', docuDt: '2024-01-16', usrNm: '김팀장' },
    { docuUuid: 'docu-002', docuStat: 'Y', docuDt: '2024-01-17', usrNm: '박부장' },
    { docuUuid: 'docu-003', docuStat: 'S', docuDt: null, usrNm: '최이사' },
  ];

  const mockApprovalLines = [
    {
      apprUuid: 'appr-001',
      apprNm: '일반 결재선',
      usrUuid1: 'user-001',
      usrUuid1Nm: '김팀장',
      usrUuid1Profile: null,
      usrUuid2: 'user-002',
      usrUuid2Nm: '박부장',
      usrUuid2Profile: null,
      usrUuid3: 'user-003',
      usrUuid3Nm: '최이사',
      usrUuid3Profile: null,
    },
    {
      apprUuid: 'appr-002',
      apprNm: '긴급 결재선',
      usrUuid1: 'user-002',
      usrUuid1Nm: '박부장',
      usrUuid1Profile: null,
      usrUuid2: 'user-003',
      usrUuid2Nm: '최이사',
      usrUuid2Profile: null,
    },
  ];

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // 컴포넌트 렌더링 함수
  const renderOldUI = (component) => (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        height: '100%',
        bgcolor: '#fff8f0',
        border: '2px dashed',
        borderColor: 'warning.main',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <OldIcon color="warning" />
        <Typography variant="subtitle2" color="warning.main">기존 UI</Typography>
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {component}
    </Paper>
  );

  const renderNewUI = (component) => (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        height: '100%',
        bgcolor: '#f0fff4',
        border: '2px solid',
        borderColor: 'success.main',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <NewIcon color="success" />
        <Typography variant="subtitle2" color="success.main">개선된 UI (V2)</Typography>
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {component}
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* 헤더 */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              수주품의서 UI 비교
            </Typography>
            <Typography variant="body2" color="text.secondary">
              기존 UI와 개선된 UI(V2)를 비교해보세요.
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="split">
              <CompareIcon sx={{ mr: 1 }} /> 나란히 보기
            </ToggleButton>
            <ToggleButton value="old">
              <OldIcon sx={{ mr: 1 }} /> 기존만
            </ToggleButton>
            <ToggleButton value="new">
              <NewIcon sx={{ mr: 1 }} /> 개선만
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {/* 개선사항 안내 */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom>주요 개선사항:</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label="카드형 레이아웃" size="small" />
          <Chip label="라벨-값 세로 배치" size="small" />
          <Chip label="상태 Chip 시각화" size="small" />
          <Chip label="매출이익 차트" size="small" />
          <Chip label="결재선 흐름 표시" size="small" />
          <Chip label="반응형 디자인" size="small" />
        </Stack>
      </Alert>

      {/* 1. 개요/요약 비교 */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        1. 개요/요약
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {(viewMode === 'split' || viewMode === 'old') && (
          <Grid item xs={12} md={viewMode === 'split' ? 6 : 12}>
            {renderOldUI(
              <OrderBookSummary 
                orderInfo={mockOrderInfo} 
                approvalList={mockApprovalList} 
              />
            )}
          </Grid>
        )}
        {(viewMode === 'split' || viewMode === 'new') && (
          <Grid item xs={12} md={viewMode === 'split' ? 6 : 12}>
            {renderNewUI(
              <OrderBookSummaryV2 
                orderInfo={mockOrderInfo} 
                approvalList={mockApprovalList}
                project={mockProject}
              />
            )}
          </Grid>
        )}
      </Grid>

      {/* 2. 기본정보 비교 */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        2. 기본정보
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {(viewMode === 'split' || viewMode === 'old') && (
          <Grid item xs={12} md={viewMode === 'split' ? 6 : 12}>
            {renderOldUI(
              <OrderBookInfo 
                orderInfo={mockOrderInfo} 
                project={mockProject}
                isLink={false}
              />
            )}
          </Grid>
        )}
        {(viewMode === 'split' || viewMode === 'new') && (
          <Grid item xs={12} md={viewMode === 'split' ? 6 : 12}>
            {renderNewUI(
              <OrderBookInfoV2 
                orderInfo={mockOrderInfo} 
                project={mockProject}
                isLink={false}
              />
            )}
          </Grid>
        )}
      </Grid>

      {/* 3. 매출이익 비교 */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        3. 매출이익 정보
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {(viewMode === 'split' || viewMode === 'old') && (
          <Grid item xs={12} md={viewMode === 'split' ? 6 : 12}>
            {renderOldUI(
              <SalesProfitInfo 
                orderInfo={mockOrderInfo} 
                project={mockProject}
              />
            )}
          </Grid>
        )}
        {(viewMode === 'split' || viewMode === 'new') && (
          <Grid item xs={12} md={viewMode === 'split' ? 6 : 12}>
            {renderNewUI(
              <SalesProfitInfoV2 
                orderInfo={mockOrderInfo} 
                project={mockProject}
              />
            )}
          </Grid>
        )}
      </Grid>

      {/* 4. 결재선 선택 비교 */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        4. 결재선 선택
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {(viewMode === 'split' || viewMode === 'old') && (
          <Grid item xs={12} md={viewMode === 'split' ? 6 : 12}>
            {renderOldUI(
              <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  * 기존 결재선 UI는 Redux 연동이 필요하여 목업으로 표시됩니다.
                </Typography>
                {/* OrderBookApprovalForm은 Redux 연동 필요 */}
                <Box sx={{ 
                  border: '1px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  p: 2,
                }}>
                  {mockApprovalLines.map((line, index) => (
                    <Box key={line.apprUuid} sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <input 
                          type="radio" 
                          name="appr" 
                          checked={selectedApprUuid === line.apprUuid}
                          onChange={() => setSelectedApprUuid(line.apprUuid)}
                        />
                        <Typography variant="subtitle2">{line.apprNm}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={2} sx={{ pl: 3, mt: 1 }}>
                        {[1,2,3,4,5].map(i => {
                          const name = line[`usrUuid${i}Nm`];
                          if (!name) return null;
                          return (
                            <Box key={i} sx={{ textAlign: 'center' }}>
                              <Box sx={{ 
                                width: 32, height: 32, 
                                borderRadius: '50%', 
                                bgcolor: 'grey.300',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 0.5,
                                fontSize: 12,
                              }}>
                                {name[0]}
                              </Box>
                              <Typography variant="caption">{name}</Typography>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Grid>
        )}
        {(viewMode === 'split' || viewMode === 'new') && (
          <Grid item xs={12} md={viewMode === 'split' ? 6 : 12}>
            {renderNewUI(
              <ApprovalLineFormV2
                approvalLines={mockApprovalLines}
                apprUuid={selectedApprUuid}
                setApprUuid={setSelectedApprUuid}
                loading={false}
              />
            )}
          </Grid>
        )}
      </Grid>

      {/* 5. 상태 Chip 비교 */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        5. 상태 표시 비교
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {(viewMode === 'split' || viewMode === 'old') && (
          <Grid item xs={12} md={viewMode === 'split' ? 6 : 12}>
            {renderOldUI(
              <Stack spacing={2}>
                <Typography variant="body2">미결재: 미결재</Typography>
                <Typography variant="body2">진행중: 진행중</Typography>
                <Typography variant="body2">완료: 완료</Typography>
                <Typography variant="body2">반려: 반려</Typography>
              </Stack>
            )}
          </Grid>
        )}
        {(viewMode === 'split' || viewMode === 'new') && (
          <Grid item xs={12} md={viewMode === 'split' ? 6 : 12}>
            {renderNewUI(
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2">미결재:</Typography>
                  <StatusChip status="N" />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2">진행중:</Typography>
                  <StatusChip status="S" />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2">완료:</Typography>
                  <StatusChip status="Y" />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2">반려:</Typography>
                  <StatusChip status="R" />
                </Stack>
              </Stack>
            )}
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default UIComparisonDemo;
